<template>
  <AiAgentSidebar
    :is-open="isOpen"
    @close="closeWindow"
  >
    <div class="ai-agent-widget__container">
      <div
        v-if="isHistoryOpen"
        class="ai-agent-widget__backdrop"
        @click="closeHistoryPanel"
      />

      <AiSessionHistoryPanel
        :is-open="isHistoryOpen"
        :sessions="sessions"
        :active-session-id="sessionId"
        :is-loading="isLoadingSessions"
        @close="closeHistoryPanel"
        @select:session="handleSelectSession"
        @delete:session="handleDeleteSession"
      />

      <AiChatWindow
        :messages="messages"
        :is-typing="isTyping"
        :is-loading-messages="isLoadingMessages"
        class="ai-agent-widget__window"
        @send:message="handleSendMessage"
        @close:window="closeWindow"
        @create:conversation="handleNewConversation"
        @toggle:history="handleToggleHistory"
      />
    </div>
  </AiAgentSidebar>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';

import {
  AiAgentMessageRoleEnum,
  AiAgentSocketEvent,
  IAiAgentErrorResponse,
  IAiAgentFilterResponse,
  IAiMessage,
  IAiQueryErrorResponse,
  IAiQuerySentResponse,
  IChatSession,
} from '@/bundles/AiAgent/types';
import { socketInstance } from '@/plugins/socket/socket';
import { chatSessionService } from '@/bundles/AiAgent/factory/chatSessionServiceFactory';
import { Nullable } from '@/utils/types';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import AiChatWindow from '@/bundles/AiAgent/components/AiChatWindow.vue';
import AiAgentSidebar from '@/bundles/AiAgent/components/AiAgentSidebar.vue';
import AiSessionHistoryPanel from '@/bundles/AiAgent/components/AiSessionHistoryPanel.vue';

interface IAiAgentWidgetInstance {
  messages: IAiMessage[];
  isTyping: boolean;
  unreadCount: number;
  sessionId: Nullable<number>;
  isHistoryOpen: boolean;
  sessions: IChatSession[];
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
}

export default defineComponent({
  name: 'AiAgentWidget',

  components: {
    AiChatWindow,
    AiAgentSidebar,
    AiSessionHistoryPanel,
  },

  props: {
    modelValue: Boolean,
  },

  emits: ['toggle:history', 'update:modelValue'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): IAiAgentWidgetInstance => ({
    messages: [],
    isTyping: false,
    unreadCount: 0,
    sessionId: null,
    isHistoryOpen: false,
    sessions: [],
    isLoadingSessions: false,
    isLoadingMessages: false,
  }),

  computed: {
    ...mapState({
      activeAccountId: (state: any) => state.activeAccount?.id,
    }),
    isOpen: {
      get (): boolean {
        return this.modelValue;
      },
      set (val: boolean) {
        this.$emit('update:modelValue', val);
      },
    },
  },

  watch: {
    modelValue (newVal) {
      if (newVal) {
        this.unreadCount = 0;
        this.fetchSessions();
      }
    },
  },

  mounted () {
    this.addMessage({
      id: Date.now(),
      role: AiAgentMessageRoleEnum.assistant,
      content: 'Hi! I\'m your Slick Cactus AI Agent. I can help you search properties, deals, pipelines, and availabilities. Try asking me something like "find office buildings in Dallas under 100k sf".',
      timestamp: new Date(),
    });
    this.setupSocketListeners();
  },

  beforeUnmount () {
    this.removeSocketListeners();
  },

  methods: {
    setupSocketListeners () {
      if (!socketInstance) return;

      socketInstance.on(AiAgentSocketEvent.AI_AGENT_FILTER_RESPONSE, this.handleFilterResponse);
      socketInstance.on(AiAgentSocketEvent.AI_AGENT_ERROR, this.handleErrorResponse);
      socketInstance.on(AiAgentSocketEvent.AI_QUERY_SENT, this.handleQuerySent);
      socketInstance.on(AiAgentSocketEvent.AI_QUERY_ERROR, this.handleQueryError);
    },

    removeSocketListeners () {
      if (!socketInstance) return;
      socketInstance.off(AiAgentSocketEvent.AI_AGENT_FILTER_RESPONSE, this.handleFilterResponse);
      socketInstance.off(AiAgentSocketEvent.AI_AGENT_ERROR, this.handleErrorResponse);
      socketInstance.off(AiAgentSocketEvent.AI_QUERY_SENT, this.handleQuerySent);
      socketInstance.off(AiAgentSocketEvent.AI_QUERY_ERROR, this.handleQueryError);
    },

    handleFilterResponse (response: IAiAgentFilterResponse) {
      this.isTyping = false;
      const aiMessage: IAiMessage = {
        id: Number(response.message_id),
        role: AiAgentMessageRoleEnum.assistant,
        content: response.formatted_results || `Found ${response.results_count || 0} results`,
        timestamp: new Date(response.timestamp),
        data: {
          record_type: response.record_type,
          items: response.api_results || [],
        },
        executionTimeMs: response.execution_time_ms,
      };
      this.addMessage(aiMessage);
      if (!this.isOpen) {
        this.unreadCount++;
      }
    },

    handleErrorResponse (response: IAiAgentErrorResponse) {
      this.isTyping = false;
      const errorMessage: IAiMessage = {
        id: Number(response.message_id),
        role: AiAgentMessageRoleEnum.assistant,
        content: `${response.error_message}\n\n${response.suggestion || ''}`,
        timestamp: new Date(response.timestamp),
        isError: true,
      };
      this.addMessage(errorMessage);
      if (!this.isOpen) {
        this.unreadCount++;
      }
    },

    handleQuerySent (data: IAiQuerySentResponse) {
      // TODO: replace with some status change for the message
      console.log('Query sent:', data);
    },

    handleQueryError (error: IAiQueryErrorResponse) {
      this.isTyping = false;
      const errorMessage: IAiMessage = {
        id: Date.now(),
        role: AiAgentMessageRoleEnum.assistant,
        content: `**Error**: Failed to send query. ${error.error || 'Please try again.'}`,
        timestamp: new Date(),
      };
      this.addMessage(errorMessage);
    },

    closeWindow () {
      this.isOpen = false;
    },

    async handleNewConversation () {
      if (this.sessionId) {
        this.leaveSessionRoom(this.sessionId);
      }

      this.messages = [];
      this.sessionId = null;
      this.isTyping = false;
      this.isHistoryOpen = false;

      this.addMessage({
        id: Date.now(),
        role: AiAgentMessageRoleEnum.assistant,
        content: 'Hi! I\'m your Slick Cactus AI Agent. I can help you search properties, deals, pipelines, and availabilities. Try asking me something like "find office buildings in Dallas under 100k sf".',
        timestamp: new Date(),
      });
    },

    handleToggleHistory () {
      this.isHistoryOpen = !this.isHistoryOpen;
      if (this.isHistoryOpen) {
        this.fetchSessions();
      }
      this.$emit('toggle:history', this.isHistoryOpen);
    },

    closeHistoryPanel () {
      this.isHistoryOpen = false;
    },

    async fetchSessions () {
      this.isLoadingSessions = true;
      try {
        const response = await chatSessionService.getSessions();
        this.sessions = response.sessions;
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.isLoadingSessions = false;
      }
    },

    async handleSelectSession (session: IChatSession) {
      if (this.sessionId) {
        this.leaveSessionRoom(this.sessionId);
      }

      this.sessionId = session.id;
      this.joinSessionRoom(session.id);
      this.messages = [];
      this.isTyping = false;
      this.isHistoryOpen = false;
      this.isLoadingMessages = true;

      try {
        const response = await chatSessionService.getSessionMessages(session.id);
        this.messages = response.messages;
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
        this.addMessage({
          id: Date.now(),
          role: AiAgentMessageRoleEnum.assistant,
          content: 'Failed to load conversation history. Please try again.',
          timestamp: new Date(),
          isError: true,
        });
      } finally {
        this.isLoadingMessages = false;
      }
    },

    async handleDeleteSession (session: IChatSession) {
      try {
        await chatSessionService.deleteSession(session.id);
        this.sessions = this.sessions.filter(s => s.id !== session.id);

        if (this.sessionId === session.id) {
          this.leaveSessionRoom(this.sessionId);
          this.sessionId = null;
          this.messages = [];
          this.addMessage({
            id: Date.now(),
            role: AiAgentMessageRoleEnum.assistant,
            content: 'Hi! I\'m your Slick Cactus AI Agent. I can help you search properties, deals, pipelines, and availabilities. Try asking me something like "find office buildings in Dallas under 100k sf".',
            timestamp: new Date(),
          });
        }
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
      }
    },

    async handleSendMessage (message: string) {
      if (!socketInstance) {
        // TODO: send err notification
        console.error('Socket not connected');
        return;
      }

      const userMessage: IAiMessage = {
        id: Date.now(),
        role: AiAgentMessageRoleEnum.user,
        content: message,
        timestamp: new Date(),
      };
      this.addMessage(userMessage);

      this.isTyping = true;

      if (!this.sessionId) {
        try {
          const session = await chatSessionService.createSession();
          this.sessionId = session.id;
          this.joinSessionRoom(session.id);
        } catch (error) {
          this.isTyping = false;
          this.addMessage({
            id: Date.now(),
            role: AiAgentMessageRoleEnum.assistant,
            content: 'Failed to create session. Please try again.',
            timestamp: new Date(),
            isError: true,
          });
          return;
        }
      }

      socketInstance.emit(AiAgentSocketEvent.AI_QUERY, {
        session_id: this.sessionId,
        query_text: message,
        language: 'en',
        priority: 'normal',
        include_formatting: true,
        max_results: 50,
        organization_id: this.activeAccountId,
      });
    },

    addMessage (message: IAiMessage) {
      this.messages.push(message);
    },

    joinSessionRoom (sessionId: number) {
      if (!socketInstance) return;
      socketInstance.emit(AiAgentSocketEvent.JOIN_SESSION, { session_id: sessionId });
    },

    leaveSessionRoom (sessionId: number) {
      if (!socketInstance) return;
      socketInstance.emit(AiAgentSocketEvent.LEAVE_SESSION, { session_id: sessionId });
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-agent-widget {
  &__container {
    position: relative;
    height: 100%;
    overflow: hidden;
  }

  &__backdrop {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 10;
  }

  &__window {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}
</style>
